import { motion } from "framer-motion";
import { MapPin, Clock, ArrowUpRight, Phone } from "lucide-react";
import { HOURS } from "../data/menu";
import { LINKS } from "../config";
import { useLang } from "../i18n";

const isOpenNow = () => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Bahrain" }));
  const mins = now.getHours() * 60 + now.getMinutes();
  const open = now.getDay() === 5 ? 14 * 60 : 7 * 60;
  return mins >= open && mins < 23 * 60;
};

export const Visit = () => {
  const { t, isAr } = useLang();
  const open = isOpenNow();
  return (
    <section id="visit" className="py-24 md:py-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="visit-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14"
      >
        <p className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-4">{t.visit.kicker}</p>
        <h2 className="font-cinzel font-medium text-3xl md:text-5xl text-pine tracking-wide">
          {t.visit.titleA} <span className="font-display italic font-light text-gold-shimmer tracking-tight">{t.visit.titleB}</span>
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="bg-parchment border border-line p-10"
          data-testid="visit-hours-card"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3 text-smoke">
              <Clock size={16} className="text-gold" />
              <span className="text-xs uppercase tracking-[0.25em]">{t.visit.hours}</span>
            </div>
            <span
              className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${
                open ? "border-pine/50 text-pine" : "border-line text-smoke"
              }`}
              data-testid="open-status-badge"
            >
              {open ? t.visit.open : t.visit.closed}
            </span>
          </div>
          {HOURS.map((h) => (
            <div key={h.days} className="flex items-baseline justify-between py-4 border-b border-line last:border-0">
              <span className="font-display text-xl text-pine">{isAr ? h.daysAr : h.days}</span>
              <span className="text-smoke text-sm tracking-wide">{isAr ? h.timeAr : h.time}</span>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="bg-parchment border border-line p-10 flex flex-col justify-between"
          data-testid="visit-location-card"
        >
          <div>
            <div className="flex items-center gap-3 text-smoke mb-8">
              <MapPin size={16} className="text-gold" />
              <span className="text-xs uppercase tracking-[0.25em]">{t.visit.location}</span>
            </div>
            <p className="font-display text-2xl md:text-3xl text-ink leading-snug">
              {t.visit.address1}
              <br />
              {t.visit.address2}
            </p>
            <p className={`${isAr ? "font-display italic" : "font-arabic"} text-gold/90 text-lg mt-4`}>{t.visit.addressAr}</p>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-8">
            <a
              href={LINKS.maps}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-button"
              className="inline-flex items-center gap-2 text-pine text-xs uppercase tracking-[0.2em] hover:gap-4 hover:text-gold transition-all duration-300 w-fit"
            >
              {t.visit.directions} <ArrowUpRight size={14} className={isAr ? "-scale-x-100" : ""} />
            </a>
            <a
              href={LINKS.phone}
              data-testid="call-button"
              className="inline-flex items-center gap-2 text-pine text-xs uppercase tracking-[0.2em] hover:text-gold transition-colors duration-300 w-fit"
            >
              <Phone size={14} /> <span dir="ltr">7777 9990</span>
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="mt-5 border border-line overflow-hidden bg-parchment"
        data-testid="visit-map"
      >
        <iframe
          title="Sipsation Coffee location map"
          src="https://www.google.com/maps?q=Sipsation%20Coffee%20Manama%20Bahrain&output=embed"
          className="w-full h-[320px] md:h-[440px] border-0"
          style={{ filter: "grayscale(0.15) contrast(1.02)" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </motion.div>
    </section>
  );
};
